import { GridColFullWidth } from '@constants/layout';
import { PlanCardType, PricingMainType } from '@lib/sanity/queries/modules/pricingMain/types';
import PortableText, { TextVariant } from '@ui/Text/PortableText';
import cn from '@utils/cn';
import { usePricingFrequency } from '../PricingMain/PricingFrequencyContext';
import MostPopularTag from './MostPopularTag';
import PlanCTAButton from './PlanCTAButton';
import PlanFeatureVariant from './PlanFeatureVariant';
import PlanPrice from './PlanPrice';

type Props = {
  data: PricingMainType['cardsPricing'];
  activeTabIndex: number;
};

const PlansMobile = ({ data, activeTabIndex }: Props) => {
  const { isYearlyFrequency } = usePricingFrequency();
  const mostPopularIndex = data?.[0]?.mostPopularIndex ?? 0;
  const mostPopularTextColor = data?.[0]?.mostPopularChipColorText?.rgb;
  const mostPopularChipColor = data?.[0]?.mostPopularChipColor?.rgb;

  // Helper function to check if discount should be applied for a plan card
  const hasActiveDiscount = (planCard: PlanCardType) => {
    if (planCard.discount) {
      const { discountLabel, discountFrequency } = planCard.discount;
      const shouldApplyDiscount =
        discountFrequency === 'both' ||
        (isYearlyFrequency && discountFrequency === 'yearly') ||
        (!isYearlyFrequency && discountFrequency === 'monthly');

      return shouldApplyDiscount && discountLabel;
    }
    return false;
  };

  const getTagLabel = (planCard: PlanCardType) => {
    if (hasActiveDiscount(planCard) && planCard.discount?.discountLabel) {
      return planCard.discount.discountLabel;
    }
    return data?.[0]?.mostPopularLabel;
  };

  const shouldShowTag = (planCard: PlanCardType, index: number) => {
    return hasActiveDiscount(planCard) || mostPopularIndex === index;
  };

  return (
    <div className={cn('flex flex-col items-center justify-center', GridColFullWidth)}>
      {data?.[0]?.planCard?.map((planCard, index) => {
        const isActive = index === activeTabIndex;
        return (
          <div className="relative flex w-full flex-col" key={`plan-${index}`}>
            <div className="flex w-full flex-col gap-3">
              <div
                className={cn({ hidden: !isActive }, 'rounded-2xl p-6')}
                style={
                  planCard.planCardColor?.rgb && planCard.planCardTopBorderColor?.rgb
                    ? {
                        backgroundColor: `rgba(${planCard.planCardColor.rgb.r},${planCard.planCardColor.rgb.g},${planCard.planCardColor.rgb.b},${planCard.planCardColor.rgb.a})`,
                        borderColor: `rgb(${planCard.planCardTopBorderColor.rgb.r},${planCard.planCardTopBorderColor.rgb.g},${planCard.planCardTopBorderColor.rgb.b},${planCard.planCardTopBorderColor.rgb.a})`,
                        borderWidth: '1px',
                      }
                    : undefined
                }
              >
                <div className="flex h-full flex-col items-start gap-6">
                  <div className="flex flex-col items-start gap-3">
                    <div className="flex w-full flex-col items-center justify-center gap-3 sm:flex-row sm:justify-start">
                      <PortableText
                        content={planCard.title}
                        variant={TextVariant.TitleXS}
                        className="font-bold text-gray-900"
                      />
                      {shouldShowTag(planCard, index) && (
                        <MostPopularTag
                          mostPopularChipColor={
                            hasActiveDiscount(planCard) && planCard.discount?.discountLabelColor?.rgb
                              ? planCard.discount.discountLabelColor.rgb
                              : mostPopularChipColor
                          }
                          mostPopularTextColor={mostPopularTextColor}
                          content={getTagLabel(planCard)}
                        />
                      )}
                    </div>
                    <PortableText
                      content={planCard.description}
                      variant={TextVariant.TextBodyMD}
                      className="text-center text-gray-500 sm:text-left"
                    />
                  </div>
                  <PlanPrice planCard={planCard} isMobile />
                  <div className="flex w-full flex-col gap-3">
                    <PortableText
                      content={planCard.keyInclusions}
                      variant={TextVariant.TextBodyMD}
                      className="font-semibold text-gray-700"
                    />
                    {planCard.keyInclusionsList?.map((feature, featIndex) => (
                      <PlanFeatureVariant
                        data={feature}
                        index={featIndex}
                        key={featIndex}
                        textVariant={TextVariant.TextLabel}
                      />
                    ))}
                  </div>
                  <div className="h-px w-full bg-gray-200" />
                  <div className="flex w-full flex-col gap-3">
                    <PortableText
                      content={planCard.featuresTitle}
                      variant={TextVariant.TextBodyMD}
                      className="font-semibold text-gray-700"
                    />
                    {planCard.planFeatures?.map((feature, featIndex) => (
                      <PlanFeatureVariant data={feature} index={featIndex} key={`feature-${featIndex}`} />
                    ))}
                  </div>
                  <div className="mt-auto w-full">
                    <PlanCTAButton planCard={planCard} />
                  </div>
                </div>
              </div>
              {isActive && (
                <PortableText
                  className="flex w-full justify-end text-sm text-gray-500"
                  variant={TextVariant.TextDescription}
                  content={data[0].allPricesAreInCurrency}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PlansMobile;
